import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FiArrowRight } from "react-icons/fi";
import { FaTicketAlt } from "react-icons/fa";

import {
  formatNewsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
} from "../../utils/utils";

/*
 * Kartica novosti, v2 spec 6.2 (deli se između stranice novosti,
 * početne i bend detalja).
 * - datum pilula gore levo, kategorija pilula gore desno, ISTI tamni stil
 * - bend link → naslov (max 2 reda) → excerpt (max 3 reda) → CTA
 * - sve kartice iste visine (CTA na dnu)
 */

const NewsCard = ({ news }) => {
  const navigate = useNavigate();

  const TypeIcon = getNewsTypeIcon(news.type);
  const ownerName = news.band?.name || news.festival?.name || "";
  const ownerPath = news.band?.id
    ? `/bend/${news.band.slug || news.band.id}`
    : news.festival?.id
      ? `/festival/${news.festival.slug || news.festival.id}`
      : null;

  const image =
    news.mainImage ||
    news.newsImages?.find((img) => img.isMain)?.url ||
    news.newsImages?.[0]?.url ||
    null;

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const openDetail = () => goTo(`/novost/${news.slug || news.id}`);

  return (
    <Card onClick={openDetail}>
      <ImageWrap $hasImage={Boolean(image)}>
        {image && <img src={image} alt={news.title} loading="lazy" />}
        {news.date && <DatePill>{formatNewsDate(news.date)}</DatePill>}
        {news.type && (
          <TypePill>
            <TypeIcon size={12} />
            {getNewsTypeLabel(news.type)}
          </TypePill>
        )}
      </ImageWrap>

      <Body>
        {ownerName && (
          <OwnerLink
            onClick={(e) => {
              e.stopPropagation();
              if (ownerPath) goTo(ownerPath);
            }}
          >
            {ownerName}
          </OwnerLink>
        )}
        <Title>{news.title}</Title>
        {news.text && <Excerpt>{news.text}</Excerpt>}

        <CtaRow>
          <GhostCta
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDetail();
            }}
          >
            Opširnije <FiArrowRight size={14} />
          </GhostCta>
          {news.ticketLink && (
            <TicketCta
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.open(news.ticketLink, "_blank", "noopener,noreferrer");
              }}
            >
              <FaTicketAlt size={12} /> Karte
            </TicketCta>
          )}
        </CtaRow>
      </Body>
    </Card>
  );
};

export default NewsCard;

/* ------------------------------- stilovi ------------------------------- */

const Card = styled.article`
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
    transform: translateY(-2px);
  }
`;

const ImageWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  background: ${(p) =>
    p.$hasImage
      ? "var(--bg-elevated)"
      : "linear-gradient(135deg, var(--bg-elevated), var(--bg-surface-2))"};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const pillBase = `
  position: absolute;
  top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.65rem;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
`;

const DatePill = styled.span`
  ${pillBase}
  left: 10px;
`;

const TypePill = styled.span`
  ${pillBase}
  right: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  svg {
    flex-shrink: 0;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1rem 1.1rem 1.1rem;
  flex: 1;
`;

const OwnerLink = styled.span`
  width: fit-content;
  color: var(--brand);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 17px;
  font-weight: 600;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Excerpt = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CtaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.9rem;
`;

const GhostCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.9rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;

const TicketCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.9rem;
  background: var(--brand);
  border: none;
  border-radius: var(--radius-control);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: var(--brand-hover);
  }
`;
